import React, { useState } from 'react';
import { Edit2, Trash2, Plus, Key, ToggleLeft, ToggleRight } from 'lucide-react';
import { useEmployees } from '../hooks/useEmployees';
import EmployeeForm from './EmployeeForm';
import type { Employee } from '../types';
import { supabase } from '../lib/supabase';

const EmployeeList: React.FC = () => {
  const { employees, loading, error, refetch } = useEmployees(true); // Include inactive employees
  const [showForm, setShowForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  const handleResetPassword = async (employee: Employee) => {
    if (!window.confirm(`Reset password for ${employee.firstName} ${employee.lastName}?`)) return;

    try {
      const defaultPassword = `${employee.firstName.charAt(0).toLowerCase()}${employee.lastName.toLowerCase()}`;
      
      const { error } = await supabase
        .rpc('reset_user_password', {
          target_email: employee.email,
          new_password: defaultPassword
        });

      if (error) throw error;
      
      alert(`Password has been reset to: ${defaultPassword}`);
    } catch (err) {
      console.error('Error resetting password:', err);
      alert(err instanceof Error ? err.message : 'Failed to reset password. Please try again.');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this employee?')) return;

    try {
      const { error: deleteError } = await supabase
        .from('employees')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;
      refetch();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'An error occurred while deleting');
    }
  };

  const handleToggleActive = async (employee: Employee) => {
    try {
      const newStatus = !employee.isActive;
      const { error } = await supabase
        .from('employees')
        .update({ is_active: newStatus })
        .eq('id', employee.id);

      if (error) throw error;
      
      refetch();
    } catch (err) {
      console.error('Error toggling employee status:', err);
      alert(err instanceof Error ? err.message : 'Failed to update employee status. Please try again.');
    }
  };

  const filteredEmployees = showInactive 
    ? employees 
    : employees.filter(employee => employee.isActive);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading employees...</div>
      </div>
    );
  }

  if (error || deleteError) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <div className="text-sm text-red-700">{error || deleteError}</div>
      </div>
    );
  }

  if (showForm || editingEmployee) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">
          {editingEmployee ? 'Edit Employee' : 'Add New Employee'}
        </h2>
        <EmployeeForm
          initialData={editingEmployee || undefined}
          onSuccess={() => {
            setShowForm(false);
            setEditingEmployee(null);
            refetch();
          }}
          onCancel={() => {
            setShowForm(false);
            setEditingEmployee(null);
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Employees</h2>
        <div className="flex items-center gap-4">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="show-inactive"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="show-inactive" className="ml-2 text-sm text-gray-700">
              Show Inactive Employees
            </label>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Plus className="h-4 w-4" />
            Add Employee
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Position
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tax Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Department
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Salary
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredEmployees.map((employee) => (
              <tr key={employee.id} className={!employee.isActive ? 'bg-gray-50' : ''}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {employee.firstName} {employee.lastName}
                      </div>
                      <div className="text-sm text-gray-500">{employee.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {employee.position}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    employee.taxStatus === '1099'
                      ? 'bg-purple-100 text-purple-800'
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {employee.taxStatus}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {employee.department}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  ${employee.salary.toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span 
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      employee.isActive 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {employee.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => handleToggleActive(employee)}
                    className={`text-blue-600 hover:text-blue-900 mr-4`}
                    title={employee.isActive ? 'Deactivate Employee' : 'Activate Employee'}
                  >
                    {employee.isActive ? (
                      <ToggleRight className="h-4 w-4" />
                    ) : (
                      <ToggleLeft className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    onClick={() => setEditingEmployee(employee)}
                    className="text-blue-600 hover:text-blue-900 mr-4"
                    title="Edit Employee"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleResetPassword(employee)}
                    className="text-blue-600 hover:text-blue-900 mr-4"
                    title="Reset Password"
                  >
                    <Key className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(employee.id)}
                    className="text-red-600 hover:text-red-900"
                    title="Delete Employee"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EmployeeList;